import { Stack, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { messages } from "../../../locales/messages";
import {
  AutocompleteOption,
  CustomAutocompleteMultiSelect,
} from "../../components/CustomAutocomplete";
import {
  GfFlyerItemCategory,
  GfStore,
  GfFigmaTemplateData,
} from "@swagger/typescript-fetch-goflyer";
import { FilterBarWrapper, FilterItem } from "./styled";

interface FilterBarProps {
  storeOptions: AutocompleteOption<GfStore>[];
  departmentOptions: AutocompleteOption<GfFlyerItemCategory>[];
  templateOptions: AutocompleteOption<GfFigmaTemplateData>[];
  selectedStores: AutocompleteOption<GfStore>[];
  selectedDepartments: AutocompleteOption<GfFlyerItemCategory>[];
  selectedTemplates: AutocompleteOption<GfFigmaTemplateData>[];
  countValue: string;
  onStoreChange: (stores: GfStore[] | undefined) => void;
  onDepartmentChange: (departments: GfFlyerItemCategory[] | undefined) => void;
  onTemplateChange: (templates: GfFigmaTemplateData[] | undefined) => void;
  onCountChange: (count: string) => void;
  isStoreLoading?: boolean;
  isDepartmentLoading?: boolean;
  isTemplateLoading?: boolean;
  onStoreSearch?: (value: string) => void;
  onDepartmentSearch?: (value: string) => void;
  onTemplateSearch?: (value: string) => void;
}

export const FilterBar = ({
  storeOptions,
  departmentOptions,
  templateOptions,
  selectedStores,
  selectedDepartments,
  selectedTemplates,
  countValue,
  onStoreChange,
  onDepartmentChange,
  onTemplateChange,
  onCountChange,
  isStoreLoading,
  isDepartmentLoading,
  isTemplateLoading,
  onStoreSearch,
  onDepartmentSearch,
  onTemplateSearch,
}: FilterBarProps) => {
  const { t } = useTranslation();

  const hasTemplateSelected = selectedTemplates && selectedTemplates.length > 0;
  const hasStoreSelected = selectedStores && selectedStores.length > 0;

  return (
    <FilterBarWrapper>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="flex-start"
        flexWrap="wrap"
        rowGap={2}
        columnGap={2}
      >
        {!hasStoreSelected && (
          <FilterItem>
            <CustomAutocompleteMultiSelect
              title={t(messages.FigmaTemplate()) as string}
              optionsList={templateOptions}
              selectedValues={selectedTemplates}
              multiSelect={false}
              handleOnChange={onTemplateChange}
              isLoading={isTemplateLoading}
              handleOnSearch={onTemplateSearch}
              dataTestId="template-filter"
              WrapperCss="min-width: 250px;"
            />
          </FilterItem>
        )}
        {!hasTemplateSelected && (
          <FilterItem>
            <CustomAutocompleteMultiSelect
              title={t(messages.Stores()) as string}
              optionsList={storeOptions}
              selectedValues={selectedStores}
              multiSelect={false}
              handleOnChange={onStoreChange}
              isLoading={isStoreLoading}
              handleOnSearch={onStoreSearch}
              dataTestId="store-filter"
              WrapperCss="min-width: 250px;"
            />
          </FilterItem>
        )}

        <FilterItem>
          <CustomAutocompleteMultiSelect
            title={t(messages.Category()) as string}
            optionsList={departmentOptions}
            selectedValues={selectedDepartments}
            multiSelect={false}
            handleOnChange={onDepartmentChange}
            isLoading={isDepartmentLoading}
            handleOnSearch={onDepartmentSearch}
            dataTestId="department-filter"
            WrapperCss="min-width: 250px;"
          />
        </FilterItem>

        <FilterItem>
          <Typography variant="body2" mb={1}>
            {t(messages.Count())}
          </Typography>
          <TextField
            size="small"
            type="number"
            value={countValue}
            onChange={(e) => {
              const newValue = e.target.value;
              if (newValue === "" || parseInt(newValue) >= 0) {
                onCountChange(newValue);
              }
            }}
            onWheel={(e) => {
              e.preventDefault();
              e.currentTarget.blur();
            }}
            onFocus={(e) => {
              e.target.addEventListener(
                "wheel",
                (event) => {
                  event.preventDefault();
                },
                { passive: false }
              );
            }}
            placeholder={t(messages.Count()) as string}
            data-testid="count-filter"
            InputProps={{
              style: { fontSize: 14 },
              inputProps: { min: 0 },
            }}
            sx={{
              minWidth: "120px",
              backgroundColor: "white",
              "& .MuiOutlinedInput-root": {
                backgroundColor: "white",
                color: "black",
                "& fieldset": {
                  borderColor: "grey.300",
                },
                "&:hover fieldset": {
                  borderColor: "#f58220",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#f58220",
                },
              },
              "& .MuiInputBase-input": {
                color: "black",
              },
            }}
          />
        </FilterItem>
      </Stack>
    </FilterBarWrapper>
  );
};
